<div class="detail-container">
  <nb-card [nbSpinner]="loader()">
    <nb-card-header>
      <button nbButton ghost status="primary" (click)="navigateBack()">
        <nb-icon pack="eva" icon="arrow-back-outline"></nb-icon>
      </button>
      <h5 class="title">
        @if (building()?.baseData?.sectionText) {
          {{ building()?.baseData?.sectionText }}
        } @else {
          Neues Gebäude
        }
      </h5>
      @if (canEdit() && !isEditing() && currentTab() !== BuildingTab.Documents) {
        <wr-edit-delete-button-group (edit)="onEdit()" (delete)="onDelete()"></wr-edit-delete-button-group>
      }
      @if (currentTab() === BuildingTab.Documents && canEditFolderContents() && !isAttachmentFolderEditing) {
        <wr-edit-delete-button-group (edit)="enableAttachmentFolders()"></wr-edit-delete-button-group>
      }
    </nb-card-header>

    <nb-card-body>
      @let tabs = activeTabs();
      @if (form()) {
        <ng-container>
          <nb-tabset (changeTab)="tabChange($event)">
            @for (tab of tabs; track tab) {
              <nb-tab [tabTitle]="buildingTabTranslations[tab]" [tabId]="tab" [active]="currentTab() === tab">
                @switch (tab) {
                  @case (BuildingTab.BuildingData) {
                    <div [formGroup]="form()">
                      <wr-building-internal-data-form formControlName="internalData" [buildingId]="buildingId()"></wr-building-internal-data-form>
                      <wr-building-base-data-form formControlName="baseData"></wr-building-base-data-form>
                      <wr-building-allocation-form formControlName="allocationData"></wr-building-allocation-form>
                      <wr-building-access-form formControlName="accessData"></wr-building-access-form>
                      <wr-building-amenity-form formControlName="amenityData"></wr-building-amenity-form>
                    </div>
                  }
                  @case (BuildingTab.Entrances) {
                    <div [formGroup]="form()">
                      <wr-building-section-editor formControlName="buildingSections"></wr-building-section-editor>
                    </div>
                  }
                  @case (BuildingTab.Units) {
                    <wr-building-flat-overview [buildingId]="buildingId()"></wr-building-flat-overview>
                  }
                  @case (BuildingTab.BillingAddresses) {
                    <div [formGroup]="form()">
                      <wr-building-billing-addresses formControlName="billingAddresses"></wr-building-billing-addresses>
                    </div>
                  }
                  @case (BuildingTab.Inspections) {
                    <wr-building-inspection-overview [buildingId]="buildingId()"></wr-building-inspection-overview>
                  }
                  @case (BuildingTab.BuildingEquipments) {
                    <wr-building-tga-overview [buildingId]="buildingId()"></wr-building-tga-overview>
                  }
                  @case (BuildingTab.Sensors) {
                    <wr-search-bar [(search)]="sensorSearch"></wr-search-bar>
                    <wr-sensor-block-list [search]="sensorSearch()" [buildingId]="buildingId()"></wr-sensor-block-list>
                  }
                  @case (BuildingTab.Scans) {
                    <wr-building-scan-list [editMode]="isEditing()" [buildingId]="buildingId()"></wr-building-scan-list>
                  }
                  @case (BuildingTab.Documents) {
                    <div [formGroup]="form()">
                      <wr-attachment-folder-tree formControlName="attachmentFolderInstances"
                                                 [allowSubFolderManagement]="canEditFolderContents() && isAttachmentFolderEditing"
                                                 (createSubFolder)="openCreateSubFolderDialog($event)"
                                                 (renameSubFolder)="openRenameSubFolderDialog($event)"
                                                 (deleteSubFolder)="confirmDeleteSubFolder($event)"></wr-attachment-folder-tree>
                    </div>
                  }
                  @case (BuildingTab.Responsibilities) {
                    <form [formGroup]="form()">
                      <div class="grid">
                        <div class="md:col-6">
                          <label class="label" for="contractorAssignments">Gewerke</label>
                          <wr-facility-element-contractor-pick-list
                            formControlName="contractorAssignments"
                            [externalTenantId]="currentExternalTenantId()">
                          </wr-facility-element-contractor-pick-list>
                        </div>
                        <div class="md:col-6">
                          <label class="label" for="assignedInternalTenants">Zuständige Firmen</label>
                          <wr-tenant-pick-list id="assignedInternalTenants" [tenantType]="TenantTypeEnum.Internal"
                                               formControlName="assignedInternalTenants"></wr-tenant-pick-list>
                        </div>
                      </div>
                    </form>
                  }
                  @case (BuildingTab.RepairTasks) {
                    <wr-building-repair-task-overview [buildingId]="buildingId()"></wr-building-repair-task-overview>
                  }
                  @case (BuildingTab.Protocols) {
                    <wr-building-protocol-overview [buildingId]="buildingId()"></wr-building-protocol-overview>
                  }
                  @case (BuildingTab.Notes) {
                    <form [formGroup]="form()">
                      <div class="grid">
                        <div class="md:col-12">
                          <label class="label" for="note">Protokoll - halten Sie hier wichtige Informationen fest</label>
                          <textarea id="note" nbInput fullWidth formControlName="note" rows="20"></textarea>
                        </div>
                      </div>
                    </form>
                  }
                  @case (BuildingTab.Settings) {
                    <form [formGroup]="form()">
                      <div class="toggle-container">
                        <label class="label" for="sendMaintenanceNotifications">Wartungstimer Erinnerungen</label>
                        <nb-toggle formControlName="sendMaintenanceNotifications" id="sendMaintenanceNotifications">
                        </nb-toggle>
                      </div>
                    </form>
                  }
                  @case (BuildingTab.ProfiNeo) {
                    <wr-building-profi-neo-sync-page [buildingId]="buildingId()"></wr-building-profi-neo-sync-page>
                  }
                  @case (BuildingTab.ProfiNeoSanierung) {
                    <wr-building-profi-neo-sanierung-sync-page
                      [buildingId]="buildingId()"
                      [internalNumber]="building()?.internalNumber ?? null"
                      [sectionCount]="buildingSectionCount()">
                    </wr-building-profi-neo-sanierung-sync-page>
                  }
                  @case (BuildingTab.SubTenant) {
                    <wr-building-subtenant-overview [buildingId]="buildingId()"></wr-building-subtenant-overview>
                  }
                  @case (BuildingTab.EmergencyContacts) {
                    <wr-emergency-contacts-tab [form]="form()"></wr-emergency-contacts-tab>
                  }
                  @case (BuildingTab.HouseCareContacts) {
                    <wr-house-care-contacts-tab [form]="form()"></wr-house-care-contacts-tab>
                  }
                  @case (BuildingTab.Contacts) {
                    <div [formGroup]="form()">
                      <wr-building-contacts formControlName="contacts"></wr-building-contacts>
                    </div>
                  }
                  @case (BuildingTab.EnergyCertificate) {
                    <wr-building-energy-pass [buildingId]="buildingId()" [editMode]="isEditing()"></wr-building-energy-pass>
                  }
                  @case (BuildingTab.HoraPass) {
                    <wr-building-hora-pass [buildingId]="buildingId()" [canEdit]="canEdit()"></wr-building-hora-pass>
                  }
                }
              </nb-tab>
            } @empty {
              <nb-tab tabTitle="Info">Ihnen wurden noch kein Zugriffe für Gebäude dieses Kunden erteilt.</nb-tab>
            }
          </nb-tabset>
        </ng-container>
      }
    </nb-card-body>


    <nb-card-footer>
      @if (currentTab() === BuildingTab.EnergyCertificate) {
        <button size="medium"
                nbButton status="primary"
                [disabled]="!isEditing() || !energyPass() || energyPass()!.saving()"
                (click)="submitEnergyPass()">
          Speichern
        </button>
        <button size="medium"
                nbButton status="basic"
                [disabled]="!isEditing() || !energyPass() || energyPass()!.saving()"
                (click)="cancelEnergyPass()">
          Abbrechen
        </button>
      } @else if (isSaveButtonVisible()) {
        @let isDocumentTab = currentTab() === BuildingTab.Documents;
        @let activeEdit = isDocumentTab ? isAttachmentFolderEditing : isEditing();

        <button size="medium"
                [disabled]="!activeEdit"
                nbButton status="primary"
                (click)="isDocumentTab ? submitAttachmentFolders() : submitBuildingData()">
          Speichern
        </button>

        <button size="medium"
                [disabled]="!activeEdit"
                nbButton status="basic"
                (click)="cancel()">
          Abbrechen
        </button>
      }
    </nb-card-footer>
  </nb-card>
</div>
